class HeaderCTAMenu {
  constructor(container) {
    this.container = container;
    this.nav = container.querySelector('.secondary-nav');
    this.overlay = document.querySelector('.js-overlay');
    if (!this.nav) return;

    this.sensitivity = Number.parseInt(container.dataset.menuSensitivity, 10) || 120;

    this.opener = null;
    this.hoverTimer = null;
    this.touchTimer = null;

    this.onClick = this.onClick.bind(this);
    this.onKeyup = this.onKeyup.bind(this);
    this.onOverlayInteract = this.onOverlayInteract.bind(this);
    this.onTransitionEnd = this.onTransitionEnd.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);

    this.onMouseEnter = this.onMouseEnter.bind(this);
    this.onMouseLeave = this.onMouseLeave.bind(this);

    this.mount();
  }

  mount() {
    this.nav.addEventListener('click', this.onClick);
    this.nav.addEventListener('keyup', this.onKeyup);
    this.nav.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.nav.addEventListener('touchend', this.onTouchEnd);

    this.container.addEventListener('transitionend', this.onTransitionEnd);
    window.addEventListener('on:breakpoint-change', this.onBreakpointChange);

    this.onCloseAll = this.onCloseAll || this.handleCloseAll.bind(this);
    window.addEventListener('nav:close-all', this.onCloseAll);

    this.bindHover();
  }

  unmount() {
    this.nav.removeEventListener('click', this.onClick);
    this.nav.removeEventListener('keyup', this.onKeyup);
    this.nav.removeEventListener('touchstart', this.onTouchStart);
    this.nav.removeEventListener('touchend', this.onTouchEnd);

    this.container.removeEventListener('transitionend', this.onTransitionEnd);
    window.removeEventListener('on:breakpoint-change', this.onBreakpointChange);

    this.unbindHover();
    this.toggleOverlay(false);
    this.unlockBodyIfNoneOpen();
  }

  canHoverOpen() {
    return !!(theme?.mediaMatches?.md && theme?.device?.hasHover);
  }

  handleCloseAll(evt) {
    if (!this.canHoverOpen()) return;
    if (evt?.detail?.source === 'cta') return;

    const active = this.nav.querySelector('details.is-open');
    if (active) this.close(active, true);
  }

  onBreakpointChange() {
    this.bindHover();
  }

  onTouchStart = () => {
    if (this.touchTimer) clearTimeout(this.touchTimer);
  };

  onTouchEnd = () => {
    if (this.touchTimer) clearTimeout(this.touchTimer);
    this.touchTimer = setTimeout(() => {}, 100);
  };

  bindHover() {
    this.unbindHover();
    if (!this.canHoverOpen()) return;

    this.hoverLinks = Array.from(this.nav.querySelectorAll('.js-nav-hover'));
    this.hoverLinks.forEach((el) => {
      el.addEventListener('mouseenter', this.onMouseEnter);
      el.addEventListener('mouseleave', this.onMouseLeave);
    });
    this.hoverBound = true;
  }

  unbindHover() {
    if (!this.hoverBound || !this.hoverLinks) return;

    this.hoverLinks.forEach((el) => {
      el.removeEventListener('mouseenter', this.onMouseEnter);
      el.removeEventListener('mouseleave', this.onMouseLeave);
    });
    this.hoverLinks = null;
    this.hoverBound = false;

    if (this.hoverTimer) clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
  }

  onMouseEnter(evt) {
    const summary = evt.currentTarget;
    if (this.hoverTimer) clearTimeout(this.hoverTimer);

    this.hoverTimer = setTimeout(() => {
      window.dispatchEvent(new CustomEvent('nav:close-all', { detail: { source: 'cta' } }));
      const details = summary.closest('details');
      if (details) this.open(details, summary);
    }, this.sensitivity);
  }

  onMouseLeave() {
    if (this.hoverTimer) clearTimeout(this.hoverTimer);
    this.hoverTimer = null;
  }

  onKeyup(evt) {
    if (evt.key === 'Escape') this.closeActive(true);
  }

  onOverlayInteract() {
    this.closeActive(true);
  }

  toggleOverlay(show) {
    if (!this.overlay) return;

    this.overlay.classList.toggle('overlay--nav', show);
    this.overlay.classList.toggle('is-visible', show);

    if (show) {
      this.overlay.addEventListener('click', this.onOverlayInteract);
      if (theme?.mediaMatches?.md) {
        this.overlay.addEventListener('mouseenter', this.onOverlayInteract);
      }
    } else {
      this.overlay.removeEventListener('click', this.onOverlayInteract);
      if (theme?.mediaMatches?.md) {
        this.overlay.removeEventListener('mouseenter', this.onOverlayInteract);
      }
    }
  }

  onClick(evt) {
    const el = evt.target;

    // Only the top-level CTA link should navigate on desktop hover
    const cta = el.closest('a.secondary-nav__cta');
    if (cta && cta.closest('summary') && this.canHoverOpen()) {
      evt.preventDefault();
      evt.stopPropagation();
      window.location.assign(cta.href);
      return;
    }

    const a = el.closest('a');
    if (a?.getAttribute('href')?.endsWith('#')) {
      evt.preventDefault();
      return;
    }

    const back = el.closest('.js-back');
    const toggle = el.closest('.js-toggle');
    if (!back && !toggle) return;

    const details = el.closest('details');
    if (!details) return;

    evt.preventDefault();

    if (back) {
      this.close(details, true);
      this.toggleOverlay(false);
      return;
    }

    this.opener = toggle;

    if (!details.classList.contains('is-open')) {
      const active = this.nav.querySelector('details.is-open');
      if (active && active !== details) this.close(active, false);
      this.open(details, toggle);
    } else {
      this.close(details, true);
      this.toggleOverlay(false);
    }
  }

  open(details, opener) {
    this.opener = opener || this.opener;
    details.open = true;

    requestAnimationFrame(() => {
      details.classList.remove('is-closing');
      details.classList.add('is-open');
    });

    if (theme?.mediaMatches?.md) this.toggleOverlay(true);

    if (typeof removeTrapFocus === 'function') removeTrapFocus();
    if (typeof trapFocus === 'function') trapFocus(details);

    document.body.classList.add('overflow-hidden');
  }

  close(details, transition = true) {
    details.classList.remove('is-open');

    if (transition) {
      details.classList.add('is-closing');
    } else {
      details.classList.remove('is-closing');
      details.open = false;
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
      this.opener = null;
    }

    queueMicrotask(() => this.unlockBodyIfNoneOpen());
  }

  closeActive(transition = true) {
    const active = this.nav.querySelector('details.is-open');
    if (!active) return;
    this.close(active, transition);
    this.toggleOverlay(false);
  }

  unlockBodyIfNoneOpen() {
    if (!this.nav.querySelector('details.is-open')) {
      document.body.classList.remove('overflow-hidden');
    }
  }

  onTransitionEnd(e) {
    if (!this.container.contains(e.target)) return;

    const details = e.target.closest('details');
    if (!details || !details.classList.contains('is-closing')) return;

    details.classList.remove('is-closing');
    details.open = false;

    if (typeof removeTrapFocus === 'function') removeTrapFocus(this.opener);
    this.opener = null;
  }
}

document.querySelectorAll('[data-header-cta-menu]').forEach((el) => {
  new HeaderCTAMenu(el);
});
